import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, GraduationCap, ArrowRight, Scale, Edit, Wallet, Gift, CheckCircle, XCircle, Calendar, CalendarCheck, BookOpen } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import { AnunciosBanner } from '../components/AnunciosBanner';
import { useAuth } from '../contexts/AuthContext';
import { goBack } from '../utils/navigation';
import { fechaToDisplay } from '../utils/fechaUtils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ColegioLandingPage() {
  const { isAuthenticated, trabajador } = useAuth();
  const [auxiliaresAbiertas, setAuxiliaresAbiertas] = useState(null);
  const [auxiliaresEstado, setAuxiliaresEstado] = useState(null); // shape completo: motivo_cierre, fecha_cierre, proxima_apertura, anio_actuacion
  const [reservasAbiertas, setReservasAbiertas] = useState(null);

  // Verificar estados de inscripciones
  useEffect(() => {
    const verificarEstado = async () => {
      try {
        const [auxRes, reservasRes] = await Promise.all([
          axios.get(`${API_URL}/auxiliares-justicia/estado-inscripcion`),
          axios.get(`${API_URL}/reservas-espacio/info`)
        ]);
        setAuxiliaresAbiertas(auxRes.data.inscripciones_abiertas);
        setAuxiliaresEstado(auxRes.data);
        setReservasAbiertas(reservasRes.data.reservas_abiertas);
      } catch (err) {
        console.error('Error verificando estados:', err);
      }
    };
    verificarEstado();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header unificado */}
      <UserHeader />

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-12">
          {/* Logo grande con animación */}
          <div className="flex justify-center mb-8 animate-fade-in">
            <img 
              src="/logo-colegio-transparent.png" 
              alt="Logo CATSLZ" 
              className="h-36 sm:h-44 lg:h-52 w-auto drop-shadow-lg hover:scale-105 transition-transform duration-500"
            />
          </div>
          
          {/* Título con efecto gradiente moderno */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 animate-slide-up">
            <span className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent">
              Servicios Digitales
            </span>
          </h1>
          
          {/* Subtítulo hero con efecto */}
          <div className="max-w-2xl mx-auto animate-slide-up animation-delay-100">
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-3">
              Ahora, vincularte con el Colegio Profesional es más fácil.
            </p>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-800 font-medium">
              Accedé a todos los servicios en un solo lugar.
            </p>
          </div>
          
          {isAuthenticated && trabajador && (
            <div className="mt-6 animate-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-medium">
                Bienvenide, {trabajador.nombre} {trabajador.apellido}
              </span>
            </div>
          )}
        </div>

        {/* Options Grid con animación staggered */}
        {isAuthenticated && <AnunciosBanner />}
        <div className={`grid gap-6 max-w-5xl mx-auto ${isAuthenticated ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-3'}`}>
          {/* Editar Mis Datos Card - Solo visible si está logueado */}
          {isAuthenticated && (
            <Link
              to="/trabajador/editar"
              className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-rose-200 flex flex-col animate-card-entrance"
              style={{ animationDelay: '0.1s' }}
              data-testid="editar-datos-card"
            >
              <div className="p-6 flex-1 flex flex-col">
                <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-rose-200 transition-all duration-300 group-hover:scale-110">
                  <Edit className="w-7 h-7 text-rose-600 transition-transform duration-300" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Editar Mis Datos
                </h2>
                <p className="text-gray-600 text-sm mb-4 flex-1">
                  Actualizá tu información personal y de contacto.
                </p>
                <div className="flex items-center text-rose-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Editar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
              <div className="h-1.5 bg-gradient-to-r from-rose-500 to-rose-600"></div>
            </Link>
          )}
          
          {/* Estado de Cuenta Card - Visible para todos */}
          <Link
            to="/estado-cuenta"
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-cyan-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.2s' }}
            data-testid="estado-cuenta-card"
          >
              <div className="p-6 flex-1 flex flex-col">
                <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-cyan-200 transition-all duration-300 group-hover:scale-110">
                  <Wallet className="w-7 h-7 text-cyan-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Estado de Cuenta
                </h2>
                <p className="text-gray-600 text-sm mb-4 flex-1">
                  Consultá tu situación de pago en el Colegio Provincial (CATSPBA).
                </p>
                <div className="flex items-center text-cyan-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Consultar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
              <div className="h-1.5 bg-gradient-to-r from-cyan-500 to-cyan-600"></div>
            </Link>

          {/* Beneficios TS+ Card - Visible para todos */}
          <Link
            to="/beneficios"
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-purple-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.3s' }}
            data-testid="beneficios-card"
          >
              <div className="p-6 flex-1 flex flex-col">
                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-all duration-300 group-hover:scale-110">
                  <Gift className="w-7 h-7 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Guía de Beneficios TS+
                </h2>
                <p className="text-gray-600 text-sm mb-4 flex-1">
                  Descuentos en hotelería, transporte, indumentaria y más para matriculados al día.
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Ver beneficios
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
              <div className="h-1.5 bg-gradient-to-r from-purple-500 to-purple-600"></div>
            </Link>
          
          {/* Recursero Card */}
          <Link
            to="/recursero"
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.4s' }}
            data-testid="recursero-card"
          >
            <div className="p-6 flex-1 flex flex-col">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-all duration-300 group-hover:scale-110">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Recursero Colaborativo
              </h2>
              <p className="text-gray-600 text-sm mb-4 flex-1">
                Directorio de colegas y guía institucional colaborativa.
              </p>
              <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                Ingresar
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </div>
            <div className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600"></div>
          </Link>

          {/* Capacitaciones Card */}
          <Link
            to="/capacitaciones"
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-emerald-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.5s' }}
            data-testid="capacitaciones-card"
          >
            <div className="p-6 flex-1 flex flex-col">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-all duration-300 group-hover:scale-110">
                <GraduationCap className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Capacitaciones
              </h2>
              <p className="text-gray-600 text-sm mb-4 flex-1">
                Cursos, talleres y certificaciones profesionales.
              </p>
              <div className="flex items-center text-emerald-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                Ver cursos
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </div>
            <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600"></div>
          </Link>

          {/* Eventos Card */}
          <Link
            to="/eventos"
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-violet-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.55s' }}
            data-testid="eventos-card"
          >
            <div className="p-6 flex-1 flex flex-col">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-violet-200 transition-all duration-300 group-hover:scale-110">
                <Calendar className="w-7 h-7 text-violet-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Eventos
              </h2>
              <p className="text-gray-600 text-sm mb-4 flex-1">
                Juras, encuentros, actividades culturales y más.
              </p>
              <div className="flex items-center text-violet-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                Ver eventos
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </div>
            <div className="h-1.5 bg-gradient-to-r from-violet-500 to-violet-600"></div>
          </Link>

          {/* iter207 · Biblioteca "Laura Iglesias" Card — unifica catálogo + reserva de espacio */}
          {isAuthenticated && (
            <Link
              to="/biblioteca"
              className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-amber-200 flex flex-col animate-card-entrance"
              style={{ animationDelay: '0.58s' }}
              data-testid="biblioteca-card"
            >
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center group-hover:bg-amber-200 transition-all duration-300 group-hover:scale-110">
                    <BookOpen className="w-7 h-7 text-amber-600" />
                  </div>
                  {/* Badge de estado de reservas de espacio */}
                  {reservasAbiertas !== null && !reservasAbiertas && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <XCircle className="w-3.5 h-3.5" />
                      Reserva de espacio pausada
                    </div>
                  )}
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Biblioteca "Laura Iglesias"
                </h2>
                <p className="text-gray-600 text-sm mb-4 flex-1">
                  Explorá el catálogo disponible y reservá turnos para usar el espacio del Colegio.
                </p>
                <div className="flex items-center text-amber-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Entrar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
              <div className="h-1.5 bg-gradient-to-r from-amber-500 to-amber-600"></div>
            </Link>
          )}

          {/* Campus Virtual Card - Visible para todos */}
          <Link
            to={isAuthenticated ? "/campus" : "/campus/acceso"}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-indigo-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.59s' }}
            data-testid="campus-card"
          >
            <div className="p-6 flex-1 flex flex-col">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-indigo-200 transition-all duration-300 group-hover:scale-110">
                <BookOpen className="w-7 h-7 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Campus Virtual
              </h2>
              <p className="text-gray-600 text-sm mb-4 flex-1">
                Aula virtual para cursos y comisiones del Colegio.
              </p>
              <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                {isAuthenticated ? 'Ingresar al campus' : 'Acceder al campus'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </div>
            <div className="h-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600"></div>
          </Link>

          {/* Auxiliares de Justicia Card */}
          <Link
            to="/auxiliares-justicia"
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-amber-200 flex flex-col animate-card-entrance"
            style={{ animationDelay: '0.6s' }}
            data-testid="auxiliares-card"
          >
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center group-hover:bg-amber-200 transition-all duration-300 group-hover:scale-110">
                  <Scale className="w-7 h-7 text-amber-600" />
                </div>
                {/* Badge de estado */}
                {auxiliaresAbiertas !== null && (
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    auxiliaresAbiertas 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {auxiliaresAbiertas ? (
                      <>
                        <CheckCircle className="w-3.5 h-3.5" />
                        Inscripciones abiertas
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3.5 h-3.5" />
                        Inscripciones cerradas
                      </>
                    )}
                  </div>
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Auxiliares de Justicia
              </h2>
              <p className="text-gray-600 text-sm mb-1 flex-1">
                Inscripción al listado oficial según Acordada 2728.
                {auxiliaresEstado?.anio_actuacion && (
                  <span className="block text-xs text-gray-500 mt-1">
                    Convocatoria {auxiliaresEstado.anio_actuacion}
                    {auxiliaresAbiertas && auxiliaresEstado.fecha_cierre && (
                      <> · cierra el {fechaToDisplay(auxiliaresEstado.fecha_cierre)}</>
                    )}
                    {!auxiliaresAbiertas && auxiliaresEstado.proxima_apertura && (
                      <> · abre el {fechaToDisplay(auxiliaresEstado.proxima_apertura)}</>
                    )}
                  </span>
                )}
              </p>
              <div className="flex items-center text-amber-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                {auxiliaresAbiertas ? 'Inscribirme' : 'Ver información'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </div>
            <div className="h-1.5 bg-gradient-to-r from-amber-500 to-amber-600"></div>
          </Link>
        </div>

        {/* Footer Links */}
        <div className="text-center mt-12 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => goBack()}
              className="inline-flex items-center justify-center px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg transition-colors"
              data-testid="back-to-website-btn"
            >
              ← Volver
            </button>
            <a 
              href="/manual_usuario.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              data-testid="manual-link"
            >
              📖 Manual de Usuario
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Colegio Profesional de Trabajadorxs Sociales - Distrito Lomas de Zamora</p>
          <p className="mt-1">servicios.colegiotslomas.org</p>
        </div>
      </footer>
    </div>
  );
}
